import React, { useCallback, useEffect } from "react";
import { useSelector, useDispatch } from "react-redux";
import { AnimatePresence, motion } from "framer-motion";
import { Box, Typography, IconButton } from "@mui/material";
import { Close, NotificationsActive } from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import {
  selectPushQueue,
  removePushNotification,
} from "../../features/slices/notificationSlice";
import PushNotificationToast from "./PushNotificationToast";
import * as Constant from "../../features/constant";

const HIGH_PRIORITY_CATEGORIES = ["PROMOTION", "REWARDS_CLUB"];
const MAX_VISIBLE_TOASTS = 3;

const LowPriorityToast = ({ notification, onDismiss }) => {
  const navigate = useNavigate();
  const subject = notification.subject || "New notification";
  const displayText = subject.length > 80 ? subject.substring(0, 80) + "…" : subject;

  useEffect(() => {
    const timer = setTimeout(() => onDismiss(notification.id), 4000);
    return () => clearTimeout(timer);
  }, [notification.id, onDismiss]);

  const handleClick = () => {
    onDismiss(notification.id);
    if (notification.category) {
      navigate(Constant.getPath(`/messaging?category=${notification.category}`));
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0, x: 300, scale: 0.95 }}
      animate={{ opacity: 1, x: 0, scale: 1 }}
      exit={{ opacity: 0, x: 300, scale: 0.95 }}
      transition={{ type: "spring", damping: 25, stiffness: 300 }}
      style={{ marginBottom: 8 }}
    >
      <Box
        onClick={handleClick}
        sx={{
          width: 300,
          background: "linear-gradient(135deg, #1a1a2e 0%, #16213e 100%)",
          borderRadius: 2,
          boxShadow: "0 4px 20px rgba(0,0,0,0.3)",
          overflow: "hidden",
          cursor: "pointer",
          display: "flex",
          alignItems: "center",
          p: 1.5,
          borderLeft: "4px solid #e2b714",
          "&:hover": {
            boxShadow: "0 6px 24px rgba(0,0,0,0.4)",
          },
        }}
      >
        <Box
          sx={{
            backgroundColor: "rgba(226, 183, 20, 0.15)",
            borderRadius: "50%",
            p: 0.8,
            mr: 1.5,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            flexShrink: 0,
          }}
        >
          <NotificationsActive sx={{ color: "#e2b714", fontSize: 20 }} />
        </Box>

        <Box flex={1} minWidth={0}>
          <Typography
            variant="caption"
            sx={{
              color: "#e2b714",
              fontWeight: 700,
              fontSize: "10px",
              textTransform: "uppercase",
              letterSpacing: 0.5,
            }}
          >
            Notification
          </Typography>
          <Typography
            variant="body2"
            sx={{
              color: "#e0e0e0",
              fontSize: "12px",
              lineHeight: 1.3,
              display: "-webkit-box",
              WebkitLineClamp: 2,
              WebkitBoxOrient: "vertical",
              overflow: "hidden",
            }}
          >
            {displayText}
          </Typography>
        </Box>

        <IconButton
          size="small"
          onClick={(e) => {
            e.stopPropagation();
            onDismiss(notification.id);
          }}
          sx={{ ml: 0.5, p: 0.3, color: "#888", flexShrink: 0 }}
        >
          <Close sx={{ fontSize: 14 }} />
        </IconButton>
      </Box>
    </motion.div>
  );
};

const PushNotificationManager = () => {
  const dispatch = useDispatch();
  const pushQueue = useSelector(selectPushQueue);

  const handleDismiss = useCallback(
    (id) => {
      dispatch(removePushNotification(id));
    },
    [dispatch]
  );

  const isHighPriority = (item) =>
    item.priority === "HIGH" || HIGH_PRIORITY_CATEGORIES.includes(item.category);

  const highPriorityItems = pushQueue
    .filter(isHighPriority)
    .slice(0, MAX_VISIBLE_TOASTS);

  const lowPriorityItems = pushQueue
    .filter((item) => !isHighPriority(item))
    .slice(0, MAX_VISIBLE_TOASTS);

  if (highPriorityItems.length === 0 && lowPriorityItems.length === 0) return null;

  return (
    <Box
      sx={{
        position: "fixed",
        top: 80,
        right: 16,
        zIndex: 1400,
        display: "flex",
        flexDirection: "column",
        alignItems: "flex-end",
      }}
    >
      <AnimatePresence>
        {highPriorityItems.map((notification) => (
          <PushNotificationToast
            key={notification.id}
            notification={notification}
            onDismiss={handleDismiss}
          />
        ))}
        {lowPriorityItems.map((notification) => (
          <LowPriorityToast
            key={notification.id}
            notification={notification}
            onDismiss={handleDismiss}
          />
        ))}
      </AnimatePresence>
    </Box>
  );
};

export default PushNotificationManager;
